import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LayoutTemplate } from 'lucide-react';
import type { APosCustomerDisplayConfig } from '@platform/shared-types';
import { InventoryPage, InventorySubtabs } from '../inventory/InventoryChrome';
import { RubyLoader } from '../../components/RubyLoader';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import LayoutEditor from '../../components/posDefaults/components/pos/LayoutEditor';
import { BUTTON_REGISTRY, type HeaderButtonConfig } from '../../components/posDefaults/config/buttonRegistry';
import { defaultCustomerDisplayConfig } from '../../components/posDefaults/components/pos/customerFacing/defaults';
import PosReceiptEditor, { type PosDefaultsMeta } from '../../components/posDefaults/pages/pos/PosReceiptEditor';

/**
 * POS DEFAULTS — the GLOBAL RubyTill layout + receipt every business falls back
 * to until it saves its own. The two editors are 1:1 copies of what a merchant
 * sees in platform-app (`/pos/terminal?tab=layout` template editor and
 * `?tab=receipt`), see `components/posDefaults/`. Saving writes the default
 * (super-admin-ext2 `/admin/pos-defaults/*`); nothing is pushed to tills.
 */

type TabId = 'layout' | 'receipt';

interface LayoutShape {
  pinned: string[];
  keypadSlots: Record<string, { action: string; label?: string } | null>;
  buttonCustomizations: Record<string, any>;
  /** POS Header buttons (Scan / Hold / Open orders). Absent = the till's default header. */
  headerButtons?: HeaderButtonConfig[];
}

interface LayoutDefault {
  layout: LayoutShape;
  customerDisplayConfig: APosCustomerDisplayConfig | undefined;
  theme: 'light' | 'dark';
}

/** Every button the platform has — what a business may show is narrowed by its packages at runtime. */
const ALL_BUTTON_IDS = Object.keys(BUTTON_REGISTRY);

/** The default never carries one store's logo or name. */
function scrubCustomerDisplay(cfg: APosCustomerDisplayConfig): APosCustomerDisplayConfig {
  return { ...cfg, idle: { ...cfg.idle, logoUrl: null, storeName: '' } };
}

function MetaLine({ meta }: { meta: PosDefaultsMeta | null }) {
  if (!meta) return null;
  if (meta.source !== 'stored' || !meta.updatedAt) {
    return <span className="text-xs text-stone-500">Built-in default (never edited)</span>;
  }
  const when = new Date(meta.updatedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  return (
    <span className="text-xs text-stone-500">
      Last saved {when}{meta.updatedBy ? <> by <span className="font-medium text-stone-700">{meta.updatedBy}</span></> : null}
    </span>
  );
}

const PosDefaultsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: TabId = searchParams.get('tab') === 'receipt' ? 'receipt' : 'layout';
  const setTab = (t: string) => setSearchParams(t === 'layout' ? {} : { tab: t }, { replace: true });

  // ---- Layout default ----
  const [layoutDefault, setLayoutDefault] = useState<LayoutDefault | null>(null);
  const [layoutLoading, setLayoutLoading] = useState(true);
  const [layoutError, setLayoutError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [layoutMeta, setLayoutMeta] = useState<PosDefaultsMeta | null>(null);
  const [receiptMeta, setReceiptMeta] = useState<PosDefaultsMeta | null>(null);
  // LayoutEditor's Save can fire onSave then onSaveTheme back to back — the
  // second PUT must build on the first, not on a stale render's copy.
  const savedRef = useRef<LayoutDefault | null>(null);

  const loadLayout = useCallback(async () => {
    setLayoutLoading(true);
    setLayoutError(null);
    try {
      const r = await api.posDefaults.getLayout();
      const cfd = r?.customerDisplayConfig && Object.keys(r.customerDisplayConfig).length > 0
        ? (r.customerDisplayConfig as APosCustomerDisplayConfig)
        : undefined;
      const next: LayoutDefault = {
        layout: {
          pinned: r?.layout?.pinned || [],
          keypadSlots: r?.layout?.keypadSlots || {},
          buttonCustomizations: r?.layout?.buttonCustomizations || {},
          ...(Array.isArray(r?.layout?.headerButtons) ? { headerButtons: r.layout.headerButtons as HeaderButtonConfig[] } : {}),
        },
        customerDisplayConfig: cfd,
        theme: r?.theme === 'dark' ? 'dark' : 'light',
      };
      savedRef.current = next;
      setLayoutDefault(next);
      setLayoutMeta({ updatedAt: r?.updatedAt ?? null, updatedBy: r?.updatedBy ?? null, source: r?.source === 'stored' ? 'stored' : 'code' });
    } catch (err: any) {
      console.error('[pos-defaults] layout load failed', err);
      setLayoutError(err?.message || 'Could not load the layout default.');
    } finally {
      setLayoutLoading(false);
    }
  }, []);

  useEffect(() => { loadLayout(); }, [loadLayout]);

  const putLayout = useCallback(async (patch: Partial<LayoutDefault>, what: string) => {
    const base = savedRef.current;
    if (!base) return;
    const next: LayoutDefault = { ...base, ...patch };
    setSaving(true);
    try {
      const r = await api.posDefaults.putLayout({
        layout: next.layout,
        customerDisplayConfig: scrubCustomerDisplay(next.customerDisplayConfig ?? defaultCustomerDisplayConfig()),
        theme: next.theme,
      });
      savedRef.current = next;
      setLayoutDefault(next);
      setLayoutMeta({ updatedAt: r?.updatedAt ?? Date.now(), updatedBy: r?.updatedBy ?? null, source: 'stored' });
      toastService.success(`${what} saved — every business without its own layout now uses it`);
    } catch (err: any) {
      console.error('[pos-defaults] layout save failed', err);
      toastService.error(err?.message ? `Couldn't save the ${what.toLowerCase()} — ${err.message}` : `Couldn't save the ${what.toLowerCase()}. Please try again.`);
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  const handleSaveLayout = useCallback(
    (layout: LayoutShape) => putLayout({ layout }, 'Layout default').catch(() => undefined),
    [putLayout],
  );
  const handleSaveCustomerDisplay = useCallback(
    (cfg: APosCustomerDisplayConfig) => putLayout({ customerDisplayConfig: cfg }, 'Customer display default').catch(() => undefined),
    [putLayout],
  );
  const handleSaveTheme = useCallback(
    (theme: 'light' | 'dark') => putLayout({ theme }, 'POS theme default').catch(() => undefined),
    [putLayout],
  );

  const meta = tab === 'layout' ? layoutMeta : receiptMeta;

  return (
    <InventoryPage
      icon={LayoutTemplate}
      title="POS Defaults"
      crumb="Deployment & Release"
      subtitle="Layout and receipt every new till starts on"
      wide
      tabs={(
        <InventorySubtabs
          tabs={[{ id: 'layout', label: 'Layout' }, { id: 'receipt', label: 'Receipt' }]}
          active={tab}
          onChange={setTab}
        />
      )}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-stone-200 bg-white px-4 py-3">
        <p className="text-sm text-stone-600">
          The RubyTill default for every business that hasn&apos;t saved its own. New tills start on it.
          Buttons a business hasn&apos;t bought are hidden automatically.
        </p>
        <MetaLine meta={meta} />
      </div>

      {tab === 'layout' && (
        layoutLoading ? (
          <RubyLoader variant="page" />
        ) : layoutError || !layoutDefault ? (
          <div className="rounded-2xl border border-stone-200 bg-white p-6 text-center">
            <p className="text-sm font-semibold text-stone-800 mb-1">Couldn&apos;t load the layout default</p>
            <p className="text-xs text-stone-500 mb-4">{layoutError}</p>
            <button
              type="button"
              onClick={loadLayout}
              className="min-h-[44px] px-5 rounded-lg bg-black text-white text-sm font-semibold hover:bg-stone-800"
            >
              Try again
            </button>
          </div>
        ) : (
          <LayoutEditor
            availableIds={ALL_BUTTON_IDS}
            initialPinned={layoutDefault.layout.pinned}
            initialKeypadSlots={layoutDefault.layout.keypadSlots}
            initialCustomizations={layoutDefault.layout.buttonCustomizations}
            initialHeaderButtons={layoutDefault.layout.headerButtons}
            onSave={handleSaveLayout}
            saving={saving}
            saveLabel="Save Default"
            customerDisplayConfig={layoutDefault.customerDisplayConfig}
            terminalType=""
            onSaveCustomerDisplay={handleSaveCustomerDisplay}
            savingCustomerDisplay={saving}
            posTheme={layoutDefault.theme}
            onSaveTheme={handleSaveTheme}
            savingTheme={saving}
            themeVariant="template"
          />
        )
      )}

      {tab === 'receipt' && <PosReceiptEditor onMeta={setReceiptMeta} />}
    </InventoryPage>
  );
};

export default PosDefaultsPage;
